import React, { useState, useCallback, useRef, useEffect } from 'react';
import cn from 'classnames';
import moment from 'moment';
import _ from 'lodash';
import { fetchLatestStyleAudit } from '../../api/style_audit';
import './StyleAudit.scss';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'component', label: 'Components' },
  { key: 'element', label: 'HTML Elements' },
  { key: 'icon', label: 'FontAwesome Icons' },
  { key: 'variable', label: 'Variables' },
  { key: 'placeholder', label: 'Placeholders' }
];

const SORT_OPTIONS = [
  { value: 'count-desc', label: 'Most used' },
  { value: 'count-asc', label: 'Least used' },
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'files-desc', label: 'Most files' }
];

const BADGE_LABELS = { variable: 'var', placeholder: '%', component: 'cmp', element: 'html', icon: 'fa' };

const COLOR_RE = /^(#[0-9a-f]{3,8}|rgba?\(.+\)|hsla?\(.+\)|white|black|transparent)$/i;
function looksLikeColor(v) {
  return v && COLOR_RE.test(v.trim());
}

const GITHUB_BASE = 'https://github.com/ask-emma/askemma-web/blob/master/';

function normalizePath(p) {
  const idx = p.indexOf('askemma-web/');
  return idx !== -1 ? p.slice(idx + 'askemma-web/'.length) : p;
}

function githubUrl(filePath, line) {
  return `${GITHUB_BASE}${normalizePath(filePath)}${line ? `#L${line}` : ''}`;
}

function getFeatureArea(path) {
  const parts = path.split('/');
  const ci = parts.indexOf('Components');
  if (ci !== -1 && ci + 1 < parts.length) return parts[ci + 1];
  const pi = parts.indexOf('Pages');
  if (pi !== -1 && pi + 1 < parts.length) return parts[pi + 1];
  if (parts.includes('styles')) return 'Shared Styles';
  if (path.includes('admin/src')) {
    const sub = path.split('admin/src/')[1]?.split('/') || [];
    const ai = sub.indexOf('components');
    if (ai !== -1 && ai + 1 < sub.length) return 'Admin: ' + sub[ai + 1];
    return 'Admin: ' + (sub[0] || 'other');
  }
  return 'Other';
}

function getApp(path) {
  return path.includes('admin/') ? 'admin' : 'react';
}

function processLocations(locs) {
  const fa = {};
  const apps = { react: 0, admin: 0 };
  const mappedLocs = [];
  for (const loc of locs) {
    const np = normalizePath(loc.file);
    const area = getFeatureArea(np);
    const app = getApp(np);
    fa[area] = (fa[area] || 0) + 1;
    apps[app] += 1;
    mappedLocs.push([np, loc.line]);
  }
  const sortedFa = Object.fromEntries(Object.entries(fa).sort((a, b) => b[1] - a[1]));
  return { apps, featureAreas: sortedFa, locations: mappedLocs };
}

function processRawData(raw) {
  const items = [];
  for (const section of ['components', 'scssTokens', 'fontAwesomeIcons']) {
    const bucket = raw[section];
    if (!bucket) continue;
    const isObject = !Array.isArray(bucket);
    const entries = isObject ? Object.entries(bucket) : bucket.map(d => [d.n || d.name, d]);

    for (const [key, val] of entries) {
      if (isObject) {
        const { apps, featureAreas, locations } = processLocations(val.locations || []);
        const tokenType =
          section === 'fontAwesomeIcons' ? 'icon' : key.startsWith('$') ? 'variable' : key.startsWith('%') ? 'placeholder' : 'component';
        items.push({
          name: key,
          count: val.count,
          type: tokenType,
          apps,
          featureAreas,
          locations,
          value: val.value || null,
          props: val.props || null
        });
      } else {
        items.push(val);
      }
    }
  }

  if (raw.htmlElements) {
    for (const [key, val] of Object.entries(raw.htmlElements)) {
      const { apps, featureAreas, locations } = processLocations(val.locations || []);
      items.push({ name: `<${key}>`, count: val.count, type: 'element', apps, featureAreas, locations, value: null, props: null });
    }
  }

  return items;
}

const StyleAudit = () => {
  const [allItems, setAllItems] = useState([]);
  const [unusedTokens, setUnusedTokens] = useState([]);
  const [maxCount, setMaxCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('count-desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFeatureArea, setActiveFeatureArea] = useState(null);
  const [expandedName, setExpandedName] = useState(null);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [scannedAt, setScannedAt] = useState(null);
  const [dataSource, setDataSource] = useState(null); // 'api' | 'file'
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [unusedExpanded, setUnusedExpanded] = useState(false);
  const [unusedFilter, setUnusedFilter] = useState('all');
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const hasData = allItems.length > 0;

  const loadData = useCallback((raw, source, fileName) => {
    if (!raw || (!raw.components && !raw.scssTokens && !raw.htmlElements && !raw.fontAwesomeIcons)) {
      setError('JSON must have a "components", "scssTokens", "htmlElements", or "fontAwesomeIcons" key.');
      return;
    }
    const items = processRawData(raw);
    if (!items.length) {
      setError('No tokens, components, or FontAwesome icons found in the file.');
      return;
    }
    const sorted = _.orderBy(items, 'count', 'desc');
    setAllItems(sorted);
    setUnusedTokens(raw.unusedTokens || []);
    setMaxCount(Math.max(...sorted.map(d => d.count)));
    setLoadedFileName(fileName || '');
    setScannedAt(raw.scannedAt || null);
    setDataSource(source);
    setActiveTab('all');
    setSortBy('count-desc');
    setSearchQuery('');
    setDebouncedSearch('');
    setActiveFeatureArea(null);
    setExpandedName(null);
    setError('');
  }, []);

  useEffect(() => {
    fetchLatestStyleAudit()
      .then(resp => {
        if (resp?.audit) loadData(resp.audit, 'api');
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = useCallback(
    file => {
      if (!file) return;
      setError('');
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const raw = JSON.parse(reader.result);
          loadData(raw, 'file', file.name);
        } catch (e) {
          setError(e.message);
        }
      };
      reader.onerror = () => setError('Failed to read file.');
      reader.readAsText(file);
    },
    [loadData]
  );

  const resetDashboard = () => {
    setAllItems([]);
    setUnusedTokens([]);
    setMaxCount(0);
    setLoadedFileName('');
    setScannedAt(null);
    setDataSource(null);
    setError('');
    setSearchQuery('');
    setDebouncedSearch('');
    setActiveFeatureArea(null);
    setExpandedName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Global drag/drop
  useEffect(() => {
    const onDragEnter = e => {
      e.preventDefault();
      dragCounterRef.current++;
      setIsDragging(true);
    };
    const onDragLeave = e => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDragging(false);
      }
    };
    const onDragOver = e => e.preventDefault();
    const onDrop = e => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file && (file.type === 'application/json' || file.name.endsWith('.json'))) {
        handleFile(file);
      }
    };

    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
    };
  }, [handleFile]);

  // Debounced search
  const handleSearchChange = e => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(val), 120);
  };

  // Filtering and sorting
  const filteredItems = React.useMemo(() => {
    let items = allItems;
    if (activeTab !== 'all') items = items.filter(d => d.type === activeTab);
    if (activeFeatureArea) {
      items = items.filter(d => d.featureAreas[activeFeatureArea]);
    }
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      items = items.filter(
        d =>
          d.name.toLowerCase().includes(q) ||
          (d.value && d.value.toLowerCase().includes(q)) ||
          Object.keys(d.featureAreas).some(f => f.toLowerCase().includes(q))
      );
    }
    const countAccessor = activeFeatureArea ? d => d.featureAreas[activeFeatureArea] : d => d.count;
    switch (sortBy) {
      case 'count-desc':
        return _.orderBy(items, countAccessor, 'desc');
      case 'count-asc':
        return _.orderBy(items, countAccessor, 'asc');
      case 'name-asc':
        return _.orderBy(items, 'name', 'asc');
      case 'name-desc':
        return _.orderBy(items, 'name', 'desc');
      case 'files-desc':
        return _.orderBy(items, d => d.locations.length, 'desc');
      default:
        return items;
    }
  }, [allItems, activeTab, activeFeatureArea, debouncedSearch, sortBy]);

  // Stats
  const stats = React.useMemo(() => {
    if (!allItems.length) return [];
    const total = allItems.length;
    const uses = _.sumBy(allItems, 'count');
    const comps = allItems.filter(d => d.type === 'component').length;
    const elems = allItems.filter(d => d.type === 'element').length;
    const icons = allItems.filter(d => d.type === 'icon').length;
    const vars = allItems.filter(d => d.type === 'variable').length;
    const phs = allItems.filter(d => d.type === 'placeholder').length;
    const files = new Set(allItems.flatMap(d => d.locations.map(l => l[0]))).size;
    return [
      { value: total, label: 'Tracked', color: 'accent' },
      { value: uses.toLocaleString(), label: 'Total Uses', color: 'green' },
      { value: files, label: 'Files', color: 'blue' },
      { value: comps, label: 'Components', color: '' },
      { value: elems, label: 'HTML Elements', color: 'orange' },
      ...(icons ? [{ value: icons, label: 'FontAwesome Icons', color: 'pink' }] : []),
      { value: vars, label: 'Variables', color: '' },
      { value: phs, label: 'Placeholders', color: 'amber' },
      ...(unusedTokens.length ? [{ value: unusedTokens.length, label: 'Unused', color: 'red' }] : [])
    ];
  }, [allItems, unusedTokens]);

  // Heatmap
  const heatmapEntries = React.useMemo(() => {
    const fmap = {};
    allItems.forEach(d => {
      Object.entries(d.featureAreas).forEach(([f, c]) => {
        if (!fmap[f]) fmap[f] = { total: 0, tokens: 0 };
        fmap[f].total += c;
        fmap[f].tokens += 1;
      });
    });
    const entries = _.orderBy(
      Object.entries(fmap).map(([name, info]) => ({ name, ...info })),
      'total',
      'desc'
    );
    const maxT = entries[0]?.total || 1;
    return entries.map(e => ({ ...e, heat: (e.total / maxT).toFixed(2) }));
  }, [allItems]);

  const filteredUnused = React.useMemo(() => {
    if (unusedFilter === 'all') return unusedTokens;
    return unusedTokens.filter(t => t.type === unusedFilter);
  }, [unusedTokens, unusedFilter]);

  const filterByFeatureArea = name => {
    setActiveFeatureArea(prev => (prev === name ? null : name));
    setExpandedName(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpanded = name => setExpandedName(prev => (prev === name ? null : name));

  // Loading
  if (isLoading) {
    return (
      <div className='style-audit-outer'>
        <div className='style-audit-landing'>
          <h1>Style & Component Audit</h1>
          <p className='landing-sub'>Loading latest scan...</p>
          <div className='audit-spinner' />
        </div>
      </div>
    );
  }

  // Landing
  if (!hasData) {
    return (
      <div className='style-audit-outer'>
        <div className={cn('style-audit-drop-overlay', { visible: isDragging })}>
          <div className='drop-ring'>
            <div className='drop-icon'>&#8615;</div>
            <div className='drop-label'>Drop JSON file</div>
            <div className='drop-hint'>scan-results.json</div>
          </div>
        </div>
        <div className='style-audit-landing'>
          <h1>Style & Component Audit</h1>
          <p className='landing-sub'>
            No scan data yet. Run <strong>node node/scripts/style_audit/run_scan.js --upload</strong> or drop a local JSON file.
          </p>
          <div className='landing-dropbox'>
            <input ref={fileInputRef} type='file' accept='.json,application/json' onChange={e => handleFile(e.target.files[0])} />
            <div className='drop-icon'>&#8615;</div>
            <div className='drop-label'>
              <strong>Drop</strong> or <strong>click</strong> to load JSON
            </div>
            <div className='drop-hint'>{'Expects { components: {}, scssTokens: {}, fontAwesomeIcons: {} }'}</div>
          </div>
          {error && <div className='landing-error'>{error}</div>}
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className='style-audit-outer'>
      <div className={cn('style-audit-drop-overlay', { visible: isDragging })}>
        <div className='drop-ring'>
          <div className='drop-icon'>&#8615;</div>
          <div className='drop-label'>Drop JSON file</div>
          <div className='drop-hint'>scan-results.json</div>
        </div>
      </div>

      <div className='style-audit-dashboard'>
        {/* Header */}
        <div className='audit-header'>
          <h1>Style & Component Audit</h1>
          <span className='audit-subtitle'>{dataSource === 'api' && scannedAt ? `Scanned ${moment(scannedAt).fromNow()}` : loadedFileName}</span>
          <button className='audit-reload-btn' onClick={resetDashboard}>
            Load file
          </button>
        </div>

        {/* Stats */}
        <div className='audit-stats-row'>
          {stats.map(s => (
            <div className='audit-stat-card' key={s.label}>
              <div className={cn('stat-value', s.color)}>{s.value}</div>
              <div className='stat-label'>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className='audit-controls'>
          <div className='audit-tab-group'>
            {TABS.map(t => (
              <button
                key={t.key}
                className={cn('audit-tab-btn', { active: activeTab === t.key })}
                onClick={() => {
                  setActiveTab(t.key);
                  setExpandedName(null);
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <select className='audit-sort-select' value={sortBy} onChange={e => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className={cn('audit-area-select', { active: !!activeFeatureArea })}
            value={activeFeatureArea || ''}
            onChange={e => {
              setActiveFeatureArea(e.target.value || null);
              setExpandedName(null);
            }}
          >
            <option value=''>All areas</option>
            {heatmapEntries.map(e => (
              <option key={e.name} value={e.name}>
                {e.name} ({e.total})
              </option>
            ))}
          </select>
          <div className='audit-search-box'>
            <input type='text' value={searchQuery} onChange={handleSearchChange} placeholder='Search tokens, files, features...' />
          </div>
        </div>

        <div className='audit-results-count'>
          Showing {filteredItems.length} of {allItems.length} items
          {activeFeatureArea && (
            <>
              {' '}
              in <strong>{activeFeatureArea}</strong> ({_.sumBy(filteredItems, d => d.featureAreas[activeFeatureArea] || 0)} uses)
              <button className='clear-filter-btn' onClick={() => setActiveFeatureArea(null)}>
                &times;
              </button>
            </>
          )}
        </div>

        {/* Token List */}
        <div className='audit-token-list'>
          {filteredItems.length === 0 ? (
            <div className='audit-empty-state'>No matching tokens found.</div>
          ) : (
            filteredItems.map(d => {
              const displayCount = activeFeatureArea ? d.featureAreas[activeFeatureArea] || 0 : d.count;
              const barPct = Math.max(3, (displayCount / maxCount) * 100);
              const feats = Object.entries(d.featureAreas).slice(0, 3);
              const isExpanded = expandedName === d.name;

              return (
                <div className={cn('audit-token-card', { expanded: isExpanded })} key={d.name}>
                  <div className='token-card-header' onClick={() => toggleExpanded(d.name)}>
                    <span className={cn('token-badge', d.type)}>{BADGE_LABELS[d.type]}</span>
                    <div className='token-name-group'>
                      <span className='token-name' title={d.name}>
                        {d.name}
                      </span>
                      {d.value && (
                        <span className='token-value-preview' title={d.value}>
                          {looksLikeColor(d.value) && <span className='color-swatch' style={{ background: d.value }} />}
                          <span className='value-text'>{d.value}</span>
                        </span>
                      )}
                    </div>
                    <div className='inline-features'>
                      {feats.map(([f, c]) => (
                        <span className='ft' key={f}>
                          {f}
                          <span className='fc'>{c}</span>
                        </span>
                      ))}
                    </div>
                    <div className='app-pills'>
                      {d.apps.react > 0 && <span className='app-pill react'>{d.apps.react}</span>}
                      {d.apps.admin > 0 && <span className='app-pill admin'>{d.apps.admin}</span>}
                    </div>
                    <span className='token-count'>
                      {activeFeatureArea ? (
                        <>
                          {displayCount}
                          <span className='token-count-total'>/ {d.count}</span>
                        </>
                      ) : (
                        d.count
                      )}
                    </span>
                    <div className='token-bar'>
                      <div className='token-bar-fill' style={{ width: `${barPct}%` }} />
                    </div>
                    <span className='expand-arrow'>&#9656;</span>
                  </div>
                  {isExpanded && (
                    <div className='detail-panel'>
                      <div className='detail-inner'>
                        {d.value && (
                          <div className='detail-section detail-value-section'>
                            <div className='detail-section-title'>Resolved Value</div>
                            <div className='resolved-value-row'>
                              {looksLikeColor(d.value) && <span className='color-swatch-lg' style={{ background: d.value }} />}
                              <code className='resolved-value-code'>{d.value}</code>
                            </div>
                          </div>
                        )}
                        <div className='detail-section'>
                          <div className='detail-section-title'>Site Feature Areas ({Object.keys(d.featureAreas).length})</div>
                          <div className='feature-breakdown'>
                            {Object.entries(d.featureAreas).map(([f, c]) => (
                              <div
                                className={cn('feature-chip', { active: activeFeatureArea === f })}
                                key={f}
                                onClick={e => {
                                  e.stopPropagation();
                                  filterByFeatureArea(f);
                                }}
                              >
                                <span>{f}</span>
                                <span className='feature-chip-count'>{c}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        {d.props && Object.keys(d.props).length > 0 && (
                          <div className='detail-section'>
                            <div className='detail-section-title'>Prop Usage ({Object.keys(d.props).length} props)</div>
                            <div className='prop-breakdown'>
                              {_.orderBy(Object.entries(d.props), ([, c]) => c, 'desc').map(([propName, count]) => (
                                <div className='prop-chip' key={propName}>
                                  <span className='prop-name'>{propName}</span>
                                  <span className='prop-count'>{count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className='detail-section'>
                          <div className='detail-section-title'>Code Locations ({d.locations.length} files)</div>
                          <div className='file-list'>
                            {d.locations.map(([f, l], i) => (
                              <a className='file-entry' title={`${f}:${l}`} key={i} href={githubUrl(f, l)} target='_blank' rel='noopener noreferrer'>
                                {f}
                                <span className='ln'>:{l}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Heatmap */}
        <h2 className='audit-heatmap-title'>Feature Area Density</h2>
        <div className='audit-heatmap-grid'>
          {heatmapEntries.map(e => (
            <div
              className={cn('hm-cell', { active: activeFeatureArea === e.name })}
              key={e.name}
              style={{ '--heat': e.heat }}
              onClick={() => filterByFeatureArea(e.name)}
            >
              <div className='hm-name' title={e.name}>
                {e.name}
              </div>
              <div className='hm-count'>{e.total}</div>
              <div className='hm-label'>{e.tokens} tokens</div>
            </div>
          ))}
        </div>

        {/* Unused Tokens */}
        {unusedTokens.length > 0 && (
          <>
            <div className='unused-section-header' onClick={() => setUnusedExpanded(prev => !prev)}>
              <h2 className='audit-heatmap-title'>
                Unused Tokens
                <span className='unused-count-badge'>{unusedTokens.length}</span>
              </h2>
              <span className='unused-subtitle'>Defined but never referenced in any SCSS or JSX file</span>
              <span className={cn('expand-arrow', { expanded: unusedExpanded })}>&#9656;</span>
            </div>
            {unusedExpanded && (
              <div className='unused-section-body'>
                <div className='unused-filter-row'>
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'variable', label: 'Variables' },
                    { key: 'placeholder', label: 'Placeholders' },
                    { key: 'component', label: 'Components' }
                  ].map(f => (
                    <button key={f.key} className={cn('audit-tab-btn', { active: unusedFilter === f.key })} onClick={() => setUnusedFilter(f.key)}>
                      {f.label}
                      <span className='unused-tab-count'>
                        {f.key === 'all' ? unusedTokens.length : unusedTokens.filter(t => t.type === f.key).length}
                      </span>
                    </button>
                  ))}
                </div>
                <div className='unused-token-grid'>
                  {filteredUnused.map(t => (
                    <div className='unused-token-chip' key={t.name}>
                      <span className={cn('token-badge', t.type)}>{BADGE_LABELS[t.type]}</span>
                      <span className='unused-token-name'>{t.name}</span>
                      {t.value && (
                        <span className='unused-token-value'>
                          {looksLikeColor(t.value) && <span className='color-swatch' style={{ background: t.value }} />}
                          <span className='value-text'>{t.value}</span>
                        </span>
                      )}
                      {t.definedAt && (
                        <a
                          className='unused-def-link'
                          href={githubUrl(t.definedAt.file, t.definedAt.line)}
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={e => e.stopPropagation()}
                          title={`${normalizePath(t.definedAt.file)}:${t.definedAt.line}`}
                        >
                          &#8599;
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StyleAudit;
